import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
export function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    // Fetch unread count
    const fetchUnreadCount = async () => {
        try {
            const response = await fetch('/api/notifications/unread-count', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.count);
            }
        }
        catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };
    // Fetch unread notifications when dropdown opens
    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/notifications/unread', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setNotifications(data.notifications);
            }
        }
        catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
        finally {
            setLoading(false);
        }
    };
    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            const response = await fetch(`/api/notifications/${notificationId}/read`, {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                // Remove from local state
                setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        }
        catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };
    // Mark all as read
    const markAllAsRead = async () => {
        try {
            const response = await fetch('/api/notifications/read-all', {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                setNotifications([]);
                setUnreadCount(0);
            }
        }
        catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };
    // Poll for unread count every 30 seconds
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);
    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (isOpen && notifications.length === 0) {
            fetchNotifications();
        }
    }, [isOpen]);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);
    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical':
                return 'text-red-600';
            case 'warning':
                return 'text-amber-600';
            default:
                return 'text-blue-600';
        }
    };
    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (seconds < 60)
            return 'Just now';
        if (seconds < 3600)
            return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400)
            return `${Math.floor(seconds / 3600)}h ago`;
        return `${Math.floor(seconds / 86400)}d ago`;
    };
    return (_jsxs("div", { className: "relative", ref: dropdownRef, children: [_jsxs("button", { onClick: () => setIsOpen(!isOpen), className: "relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors", "aria-label": "Notifications", children: [_jsx("svg", { className: "w-6 h-6", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" }) }), unreadCount > 0 && (_jsx("span", { className: "absolute top-1 right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full", children: unreadCount > 99 ? '99+' : unreadCount }))] }), isOpen && (_jsxs("div", { className: "absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-card border border-gray-200 z-50", children: [_jsxs("div", { className: "p-4 border-b border-gray-200 flex items-center justify-between", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900", children: "Notifications" }), unreadCount > 0 && (_jsx("button", { onClick: markAllAsRead, className: "text-sm text-primary-600 hover:text-primary-800 font-medium", children: "Mark all as read" }))] }), _jsx("div", { className: "max-h-96 overflow-y-auto", children: loading ? (_jsxs("div", { className: "p-8 text-center", children: [_jsx("div", { className: "inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" }), _jsx("p", { className: "mt-2 text-sm text-gray-600", children: "Loading notifications..." })] })) : notifications.length === 0 ? (_jsxs("div", { className: "p-8 text-center", children: [_jsx("svg", { className: "mx-auto h-12 w-12 text-gray-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" }) }), _jsx("p", { className: "mt-2 text-sm font-medium text-gray-900", children: "No new notifications" }), _jsx("p", { className: "text-sm text-gray-500", children: "You're all caught up!" })] })) : (_jsx("div", { className: "divide-y divide-gray-100", children: notifications.map((notification) => (_jsx("div", { className: "p-4 hover:bg-gray-50 transition-colors", children: _jsx("div", { className: "flex items-start justify-between", children: _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2 mb-1", children: [_jsx("h4", { className: `text-sm font-medium ${getSeverityColor(notification.delivery_details.severity)}`, children: notification.delivery_details.title }), _jsx("span", { className: "text-xs text-gray-500", children: formatTimeAgo(notification.sent_at) })] }), _jsx("p", { className: "text-sm text-gray-700 mb-2", children: notification.delivery_details.message }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Link, { to: `/projects/${notification.project_id}`, onClick: () => {
                                                            markAsRead(notification.id);
                                                            setIsOpen(false);
                                                        }, className: "text-xs text-primary-600 hover:text-primary-800 font-medium", children: "View Project \u2192" }), _jsx("button", { onClick: () => markAsRead(notification.id), className: "text-xs text-gray-500 hover:text-gray-700", children: "Dismiss" })] })] }) }) }, notification.id))) })) }), notifications.length > 0 && (_jsx("div", { className: "p-3 border-t border-gray-200 text-center", children: _jsx(Link, { to: "/notifications", onClick: () => setIsOpen(false), className: "text-sm text-primary-600 hover:text-primary-800 font-medium", children: "View all notifications" }) }))] }))] }));
}
//# sourceMappingURL=NotificationBell.js.map